/**
 * Created by Carsten Bühler
 * User: c.buehler on 06.10.2016
 * Copyright: xdot GmbH 2015
 */

//require('external/leaflet-plugin-google');

import L from 'external/leaflet';
import { amdRequire } from 'plugin/liferay-widgets-react-web';
import { FliwasRainRadarLayer } from '../layer/FliwasRainRadarLayer';
import FliwasDataLayer from '../layer/FliwasDataLayer';
import RemoteVectorLayer from '../layer/RemoteVectorLayer';
import FeatureManager from '../utils/FeatureManager';
import CartoLayer from '../layer/CartoLayer';
import { FliwasCanvasTile } from '../FeatureRenderer/FliwasCanvasTileRenderer';

class LoadingLayer {

    constructor() {
        this.delegateCalls = [];
        [
            'on', 'off', 'setZIndex', 'clearLayers', 'onRemove', 'onAdd', 'eachLayer', 'removeFrom', 'update'
        ].forEach(fn => this[ fn ] = this.delegateFktn(fn).bind(this));
        this.getFeatureLayer = (id, classname) => (this.delegate && this.delegate.getFeatureLayer(id, classname)) || null;
    }

    //return config in loading state, currently only for zIndex
    get options() {
        return (this.delegate && this.delegate.options) || this.config;
    }

    get controller() {
        return this.delegate && this.delegate.controller;
    }

    set controller(c) {
        if (this.delegate) {
            this.delegate.controller = c;
        } else {
            this.delegateController = c;
        }
    }

    setInternalLayer(instance) {
        this.delegate = instance;
        this.delegate.id = this.id;
        this.delegate.logicalLayerId = this.logicalLayerId;
        this.delegate.typeKey = this.typeKey;
        this.delegate.controller = this.delegate.controller || this.delegateController || this.delegate.delegateController;
        this.delegateCalls.forEach(({ fn, args }) => this.callOnDelegate(fn, args));
        this.delegateCalls = [];
    }

    getInternalLayer() {
        let delegate = this.delegate;
        while (delegate != null && delegate.delegate) {
            delegate = delegate.delegate;
        }
        return delegate;
    }

    delegateFktn(fn) {
        return (...args) => {
            if (this.delegate && this.delegate[ fn ]) {
                this.delegate[ fn ].apply(this.delegate, args);
            } else {
                this.delegateCalls.push({ fn, args: args });
            }
        };
    }

    callOnDelegate(fn, args) {
        const f = this.delegate[ fn ];
        f && f.apply(this.delegate, args);
    }

}

export default function layerFactory(map, mapOptions, layer, config, plugin, context) {
    plugin = typeof plugin === 'string' && amdRequire.defined(plugin) ? amdRequire(plugin) : plugin;

    let lf = getLayerFactoryFunction(layer.typeKey);

    if (!lf) {
        return;
    }

    if (__DEV__) {
        layer = Object.freeze(layer);
        config = Object.freeze(config);
    }

    //for plugins merge config
    if (typeof plugin === 'string') {
        lf = (map, layer, config, plugin, context) => {
            const inst = new LoadingLayer();
            inst.config = config;
            amdRequire([ config.plugin ], (plugin) => {
                inst.setInternalLayer(layerFactory(map, mapOptions, layer, config, plugin, context));
            });
            return inst;
        };

    } else if (plugin) {
        const l = plugin[ config.definition ];
        layer = {
            ...layer,
            ...l
        };
        config = {
            ...config,
            ...l.config
        };
        layer.config = config;
    }

    const controller = typeof layer.controller === 'function' ? new layer.controller() : layer.controller;
    if (controller) {
        config = controller.layerConfig(config);
    }
    const inst = lf(map, layer, config, plugin, context);

    if (inst) {
        if (__DEBUG__ && !layer.id) {
            debugger;
        }
        inst.id = layer.id;
        inst.logicalLayerId = layer.logicalLayerId;
        inst.typeKey = layer.typeKey;
    }

    if (inst && controller && controller.init) {
        initController(controller, mapOptions, inst, map, config, context);
    }

    return inst;
}

function initController(controller, mapOptions, inst, map, layerConfig, context) {
    if (controller && controller.init) {
        controller.init(map, mapOptions, inst, layerConfig, context);
    }
    inst.controller = controller;
}

function createCartoLeafletLayer(cartodb, config, map, layer) {
    const cartoLayer = new CartoLayer(cartodb, config.client.username, config.client.apiKey);
    const leafletLayer = cartoLayer.createLayer(config, layer);
    leafletLayer.addTo(map);
    return leafletLayer;
}

let cartodb;

function getLayerFactoryFunction(typeKey) {

    switch (typeKey) {

        case 'cartodb': {
            return (map, l, config) => {
                /*
                    Example for a JSON object with the layer data

                    {
                        "client": {
                            "apiKey": "MY_API_KEY",
                            "username": "MY_USER_NAME"
                        },
                        "layers": [
                            {
                                "sql": "SELECT * FROM bw_fliwas_ezg_agg03_2018_02_10",
                                "style": "#bw_fliwas_ezg_agg03_2018_02_10{ polygon-fill: #FFFFFF; polygon-opacity: 0; line-color: #000000; line-width: 2.5; line-opacity: 1;}"
                            }
                        ]
                    }*/

                if (cartodb) {
                    return createCartoLeafletLayer(cartodb, config, map, l);
                } else {
                    const inst = new LoadingLayer();
                    amdRequire([ 'cartodb' ], (c) => {
                        cartodb = c;
                        let leafletLayer = createCartoLeafletLayer(cartodb, config, map, l);
                        inst.setInternalLayer(leafletLayer);
                        return inst;
                    });
                    return inst;
                }
            };
        }
        case 'wms': {
            return (map, l, config) => {
                const { legend, infoText, ...rest } = config;
                let wmsConfig = { ...rest };
                if (config.crs) {
                    let crsCode = config.crs;
                    wmsConfig.crs = L.CRS[ crsCode ];
                }

                return L.tileLayer.wms(config.url, wmsConfig).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
            };
        }
        case 'gme': {
            return (map, l, config) => L.tileLayer.wms(config.url, config).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
        }

        case 'leaflet-tile-layer': {
            return (map, l, config) => L.tileLayer(config.url, config).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
        }
        case 'geojson': {
            return (map, l, config) => L.geoJson(l.data, {
                ...config,
                //setZindex does not work on the geojson layer, only on individual paths inside the layer
                zIndex: l.zIndex || config.zIndex || 0
            }).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
        }

        case 'radar-image-layer': {
            return (map, l, config) => {
                let { url, options } = config;
                return new FliwasRainRadarLayer(url, options).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
            };
        }

        case 'remote-data-layer': {
            return (map, l, config) => {
                if (config.servicePath) {
                    /* Old service Style */
                    console.error('Old remote-data-layer config found! ', l, config);
                }
                return new FliwasDataLayer({
                    cluster: config.cluster || true,
                    clusterOptions: {
                        animate: true,
                        animateAddingMarkers: true,
                        iconCreateFunction: FeatureManager.renderCluster,
                        disableClusteringAtZoom: 10,
                        maxClusterRadius: 50, //we use more clusters than the default
                        spiderfyOnMaxZoom: false,
                        zoomToBoundsOnClick: true,
                        showCoverageOnHover: false,
                        zIndex: l.zIndex || config.zIndex || 0,
                        ...config.clusterOptions
                    }
                }).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
            };
        }

        case 'remote-vector-layer': {
            return (map, l, config) => {
                const url = window.location.origin + config.servicePath + encodeURIComponent(JSON.stringify(config.filter));
                const vectorTilesLayer = new RemoteVectorLayer(url, {
                    rendererFactory: (tileCoord, tileSize, opts) => new FliwasCanvasTile(tileCoord, tileSize, opts),
                    interactive: true,
                    ...config
                });
                vectorTilesLayer.config = config;
                map.addLayer(vectorTilesLayer.setZIndex(l.zIndex || config.zIndex || 0));
                return vectorTilesLayer;
            };
        }

        case 'kml': {
            /*
             if (layer.title == 'Wetterwarnungen') {
             console.log('Found DWD-KML-Layer. Request by Proxy.', config.url, '/dwdLayerProxy');
             config.url = location.origin + '/dwd-portlet/dwdLayerProxy';
             }
             */

            if (!L.KML) {
                //TODO  Check current implementation logic
                console.error('\'leaflet-plugin-kml\' not loaded! Check current implementation logic');
                return undefined;
                //amdRequire([ 'leaflet-plugin-kml' ], () => (map, l, config) => new L.KML(config.url, { async: true }).addTo(map).setZIndex(config.zIndex || 0));
            } else {
                return (map, l, config) => new L.KML(config.url, { async: true }).addTo(map).setZIndex(l.zIndex || config.zIndex || 0);
            }
        }

        case 'markerList': {
            return (map, l, config) => {
                let markers = l.features.map(
                    feature => L.marker([ feature.lat, feature.lng ], feature.options).setZIndexOffset(config.zIndex || 0));

                return L.featureGroup(markers).addTo(map);
            };
        }

        default:
            console.warn('No Layer of typeKey', typeKey);
            break;
    }
}



// WEBPACK FOOTER //
// ./js/utils/LayerFactory.js